import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Upload, FileText, AlertTriangle, CheckCircle, ArrowRight, ArrowLeft, 
  Download, Undo, X, Loader2, ChevronRight, Settings, Building2, DoorOpen 
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = ['upload', 'preview', 'mapping', 'confirm'];

export default function ImportCodesPage() {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);
  const isRTL = i18n.language === 'ar';
  
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Branch + Room selection (required)
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [filteredRooms, setFilteredRooms] = useState([]);

  // Mapping options
  const [mappingConfig, setMappingConfig] = useState({
    useThemeAsCategory: true,
    nameFallbackFormat: '{theme} - {code}',
  });
  
  // Undo state
  const [canUndo, setCanUndo] = useState(null);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoing, setUndoing] = useState(false);

  useEffect(() => {
    loadBranchesAndRooms();
    checkCanUndo();
  }, []);

  useEffect(() => {
    if (!selectedBranch) { setFilteredRooms([]); setSelectedRoom(''); return; }
    setSelectedRoom('');
    // Fetch rooms fresh from API when branch changes
    api.get('/rooms', { params: { branch_id: selectedBranch } })
      .then(res => setFilteredRooms(res.data))
      .catch(() => setFilteredRooms([]));
  }, [selectedBranch]);

  const loadBranchesAndRooms = async () => {
    try {
      const brRes = await api.get('/branches');
      setBranches(brRes.data);
    } catch { /* ignore */ }
  };

  const checkCanUndo = async () => {
    try {
      const { data } = await api.get('/admin/import/can-undo');
      setCanUndo(data);
    } catch {
      setCanUndo({ can_undo: false });
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error(t('import.selectCSV'));
      return;
    }
    setFile(f);
    setPreview(null);
    setImportResult(null);
  };

  const canProceedToPreview = file && selectedBranch && selectedRoom;

  const uploadAndPreview = async () => {
    if (!canProceedToPreview) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const params = new URLSearchParams({
        dry_run: 'true',
        branch_id: selectedBranch,
        room_id: selectedRoom,
        use_theme_as_category: mappingConfig.useThemeAsCategory.toString(),
        name_fallback_format: mappingConfig.nameFallbackFormat,
      });
      
      const { data } = await api.post(`/admin/import/codes?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setPreview(data);
      setCurrentStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setUploading(false);
    }
  };

  const executeImport = async () => {
    if (!file || !selectedBranch || !selectedRoom) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const params = new URLSearchParams({
        dry_run: 'false',
        branch_id: selectedBranch,
        room_id: selectedRoom,
        use_theme_as_category: mappingConfig.useThemeAsCategory.toString(),
        name_fallback_format: mappingConfig.nameFallbackFormat,
      });
      
      const { data } = await api.post(`/admin/import/codes?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setImportResult(data);
      setCurrentStep(3);
      checkCanUndo();
      toast.success(t('import.importComplete'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setImporting(false);
    }
  };

  const handleUndo = async () => {
    if (!canUndo?.import?.id) return;
    setUndoing(true);
    try {
      const { data } = await api.post(`/admin/import/logs/${canUndo.import.id}/undo`);
      toast.success(t('import.undoComplete'));
      setUndoDialogOpen(false);
      checkCanUndo();
      setImportResult({ ...importResult, undone: true, undo_result: data });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setUndoing(false);
    }
  };

  const downloadErrorCSV = () => {
    const errorContent = importResult?.error_csv || preview?.error_csv;
    if (!errorContent) return;
    const blob = new Blob([errorContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import_errors_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetWizard = () => {
    setCurrentStep(0);
    setFile(null);
    setPreview(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectedBranchName = branches.find(b => b.id === selectedBranch)?.name || '';
  const selectedRoomName = filteredRooms.find(r => r.id === selectedRoom)?.name || '';

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, idx) => (
        <div key={step} className="flex items-center">
          <button
            onClick={() => idx < currentStep && setCurrentStep(idx)}
            disabled={idx > currentStep}
            className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm transition-colors ${
              idx === currentStep ? 'bg-brand text-white'
                : idx < currentStep ? 'bg-brand/20 text-brand cursor-pointer hover:bg-brand/30'
                  : 'bg-muted text-muted-foreground'
            }`}
          >
            {idx + 1}
          </button>
          <span className={`mx-2 text-sm ${idx === currentStep ? 'text-brand font-medium' : 'text-muted-foreground'}`}>
            {t(`import.step${idx + 1}${step.charAt(0).toUpperCase() + step.slice(1)}`)}
          </span>
          {idx < STEPS.length - 1 && (
            <ChevronRight className={`w-4 h-4 mx-2 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1Upload = () => (
    <div className="space-y-6">
      {canUndo?.can_undo && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-amber-800 dark:text-amber-300">
              {t('import.canUndo')}: <strong>{canUndo.import.filename}</strong> ({formatDate(canUndo.import.created_at)})
            </span>
            <Button variant="outline" size="sm" onClick={() => setUndoDialogOpen(true)} className="border-amber-500 text-amber-700 hover:bg-amber-100">
              <Undo className="w-4 h-4 me-1" /> {t('import.undoImport')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card className="bg-card border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-barlow text-xl text-foreground">{t('import.uploadFile')}</CardTitle>
          <CardDescription>{t('import.selectCSV')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Target Branch + Room Selection */}
          <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand" />
              Target Location (Required)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Branch *</Label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch} data-testid="import-branch-select">
                  <SelectTrigger><SelectValue placeholder="Select Branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Room *</Label>
                <Select key={`room-${selectedBranch}-${filteredRooms.length}`} value={selectedRoom} onValueChange={setSelectedRoom} disabled={!selectedBranch} data-testid="import-room-select">
                  <SelectTrigger><SelectValue placeholder={selectedBranch ? "Select Room" : "Select Branch first"} /></SelectTrigger>
                  <SelectContent>
                    {filteredRooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selectedBranch && filteredRooms.length === 0 && (
                  <p className="text-xs text-amber-500">No rooms in this branch. Create rooms first.</p>
                )}
              </div>
            </div>
            {!selectedBranch || !selectedRoom ? (
              <p className="text-xs text-red-400">Select Branch and Room first before importing.</p>
            ) : (
              <p className="text-xs text-emerald-500 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                All assets will be imported into: <strong>{selectedBranchName}</strong> / <strong>{selectedRoomName}</strong>
              </p>
            )}
          </div>

          {/* File Upload Area */}
          <div 
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-brand/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileSelect} className="hidden" />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-10 h-10 text-brand" />
                <div className="text-start">
                  <p className="font-medium text-foreground">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t('attachments.dropFiles')}</p>
                <p className="text-sm text-muted-foreground mt-1">CSV {t('common.only')}</p>
              </>
            )}
          </div>

          {/* Info: CSV room column = stage */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">{t('import.instructions')}</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• CSV must have columns: branch, theme, room, code, name, description</li>
              <li>• The "room" column in CSV will be saved as <strong>Stage</strong> (not as a separate room)</li>
              <li>• All assets will be placed in the selected Branch + Room above</li>
              <li>• Duplicate codes (same branch) will update existing assets</li>
              <li>• Branches and Rooms are NOT auto-created — create them manually first</li>
            </ul>
          </div>

          <Button 
            onClick={uploadAndPreview} 
            disabled={!canProceedToPreview || uploading}
            className="w-full bg-brand hover:bg-brand-hover text-brand-foreground"
            data-testid="preview-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 me-2 animate-spin" /> {t('import.analyzing')}</>
            ) : (
              <>{t('import.previewImport')} <ArrowRight className={`w-4 h-4 ms-2 ${isRTL ? 'rotate-180' : ''}`} /></>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep2Preview = () => (
    <div className="space-y-6">
      {/* Target Info */}
      <Alert className="border-brand/30 bg-brand/5">
        <DoorOpen className="w-4 h-4 text-brand" />
        <AlertDescription className="text-foreground">
          Importing into: <strong>{selectedBranchName}</strong> / <strong>{selectedRoomName}</strong>
          {preview?.stages_found?.length > 0 && (
            <span className="ms-2">| Stages: {preview.stages_found.join(', ')}</span>
          )}
        </AlertDescription>
      </Alert>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-brand">{preview?.total_rows || 0}</p>
            <p className="text-xs text-muted-foreground">{t('import.totalRows')}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-500">{preview?.assets_created || 0}</p>
            <p className="text-xs text-muted-foreground">{t('import.newAssets')}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">{preview?.assets_updated || 0}</p>
            <p className="text-xs text-muted-foreground">{t('import.updatedAssets')}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-500">{preview?.stages_found?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Stages</p>
          </CardContent>
        </Card>
        <Card className={`bg-card border-border shadow-sm ${preview?.errors?.length > 0 ? 'border-rose-200' : ''}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${preview?.errors?.length > 0 ? 'text-rose-500' : 'text-foreground'}`}>{preview?.errors?.length || 0}</p>
            <p className="text-xs text-muted-foreground">{t('import.errors')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Warnings & Errors */}
      {preview?.warnings?.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/30">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription>
            <strong className="text-amber-800 dark:text-amber-300">{t('import.warningsFound')}:</strong>
            <ul className="mt-2 space-y-1 text-sm text-amber-700 dark:text-amber-400">
              {preview.warnings.slice(0, 5).map((w, idx) => (
                <li key={idx}>• Line {w.line}: {w.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {preview?.errors?.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            <strong>{t('import.errorsFound')}:</strong>
            <ul className="mt-2 space-y-1 text-sm">
              {preview.errors.slice(0, 5).map((e, idx) => (
                <li key={idx}>• Line {e.line}: {e.error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Preview Table */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground">{t('import.previewFirst200')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[400px]">
            <Table>
              <TableHeader className="sticky top-0 bg-muted/50">
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>{t('import.theme')}</TableHead>
                  <TableHead>{t('import.code')}</TableHead>
                  <TableHead>{t('import.assetName')}</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">{t('common.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview?.preview_rows?.map((row, idx) => (
                  <TableRow key={idx} className={!row.is_new ? 'bg-blue-50 dark:bg-blue-950/20' : ''}>
                    <TableCell className="text-muted-foreground">{row.line}</TableCell>
                    <TableCell className="text-brand font-medium">{row.stage}</TableCell>
                    <TableCell>{row.theme}</TableCell>
                    <TableCell className="font-mono text-xs">{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{row.description}</TableCell>
                    <TableCell>
                      <Badge variant={row.is_new ? 'default' : 'secondary'} className="text-xs">
                        {row.is_new ? 'New' : 'Update'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(0)}>
          <ArrowLeft className={`w-4 h-4 me-2 ${isRTL ? 'rotate-180' : ''}`} /> {t('common.back')}
        </Button>
        <Button onClick={() => setCurrentStep(2)} className="bg-brand hover:bg-brand-hover text-brand-foreground">
          {t('common.next')} <ArrowRight className={`w-4 h-4 ms-2 ${isRTL ? 'rotate-180' : ''}`} />
        </Button>
      </div>
    </div>
  );

  const renderStep3Mapping = () => (
    <div className="space-y-6">
      <Card className="bg-card border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-barlow text-xl text-foreground flex items-center gap-2">
            <Settings className="w-5 h-5 text-brand" /> {t('import.mappingOptions')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3">
            <Checkbox
              id="useThemeAsCategory"
              checked={mappingConfig.useThemeAsCategory}
              onCheckedChange={(checked) => setMappingConfig({ ...mappingConfig, useThemeAsCategory: !!checked })}
              data-testid="use-theme-checkbox"
            />
            <Label htmlFor="useThemeAsCategory" className="cursor-pointer">
              {t('import.useThemeAsCategory')}
            </Label>
          </div>
          <div className="space-y-2">
            <Label>{t('import.nameFallback')}</Label>
            <Input
              value={mappingConfig.nameFallbackFormat}
              onChange={(e) => setMappingConfig({ ...mappingConfig, nameFallbackFormat: e.target.value })}
              placeholder="{theme} - {code}"
              data-testid="name-fallback-input"
            />
            <p className="text-xs text-muted-foreground">{t('import.nameFallbackDesc')}</p>
          </div>
        </CardContent>
      </Card>

      <Alert className="border-brand/30 bg-brand/5">
        <CheckCircle className="w-4 h-4 text-brand" />
        <AlertDescription className="text-foreground">
          <strong>Summary:</strong> {preview?.assets_created} new, {preview?.assets_updated} updated
          | Target: {selectedBranchName} / {selectedRoomName}
          | {preview?.stages_found?.length || 0} stages
        </AlertDescription>
      </Alert>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(1)}>
          <ArrowLeft className={`w-4 h-4 me-2 ${isRTL ? 'rotate-180' : ''}`} /> {t('common.back')}
        </Button>
        <Button 
          onClick={executeImport} disabled={importing}
          className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-import-btn"
        >
          {importing ? (
            <><Loader2 className="w-4 h-4 me-2 animate-spin" /> {t('import.importing')}</>
          ) : (
            <><CheckCircle className="w-4 h-4 me-2" /> {t('import.confirmImport')}</>
          )}
        </Button>
      </div>
    </div>
  );

  const renderStep4Confirm = () => (
    <div className="space-y-6">
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
          <h2 className="text-2xl font-barlow font-bold text-foreground mb-2">{t('import.importComplete')}</h2>
          <p className="text-muted-foreground mb-2">
            Imported into: <strong>{selectedBranchName}</strong> / <strong>{selectedRoomName}</strong>
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {importResult?.stages_found?.length || 0} stages found: {importResult?.stages_found?.join(', ')}
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-xl mx-auto mb-6">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-2xl font-bold text-brand">{importResult?.assets_created || 0}</p>
              <p className="text-xs text-muted-foreground">{t('import.assetsCreated')}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-2xl font-bold text-brand">{importResult?.assets_updated || 0}</p>
              <p className="text-xs text-muted-foreground">{t('import.assetsUpdated')}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-2xl font-bold text-purple-500">{importResult?.stages_found?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Stages</p>
            </div>
          </div>

          {importResult?.error_csv && (
            <Button variant="outline" onClick={downloadErrorCSV} className="mb-4">
              <Download className="w-4 h-4 me-2" /> {t('import.downloadErrorCSV')}
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={resetWizard}>{t('import.importAnother')}</Button>
        {canUndo?.can_undo && (
          <Button variant="outline" onClick={() => setUndoDialogOpen(true)} className="border-amber-500 text-amber-700 hover:bg-amber-100">
            <Undo className="w-4 h-4 me-2" /> {t('import.undoImport')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="import-codes-page" className="space-y-6">
      <div>
        <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('import.title')}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t('import.subtitle')}</p>
      </div>

      {renderStepIndicator()}

      {currentStep === 0 && renderStep1Upload()}
      {currentStep === 1 && renderStep2Preview()}
      {currentStep === 2 && renderStep3Mapping()}
      {currentStep === 3 && renderStep4Confirm()}

      {/* Undo Dialog */}
      <Dialog open={undoDialogOpen} onOpenChange={setUndoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo className="w-5 h-5 text-amber-500" /> {t('import.undoImport')}
            </DialogTitle>
            <DialogDescription>
              {t('import.undoConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoDialogOpen(false)} disabled={undoing}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleUndo} disabled={undoing}>
              {undoing ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Undo className="w-4 h-4 me-2" />}
              {t('import.undoImport')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
